import { Division, GetScore, Lifter, Meet } from "types";
import { toKg, toLbs } from "util/conversions";
import { getTotal } from "util/lifterHelper";
import { isKgMeet, isLbsMeet } from "util/meetHelper";
import { competitionCode } from "util/standardDivisions";
import { ageCoef } from "./age";

export const kPointsFormula = function (
  total: number,
  division: Division | undefined,
  lifter: Lifter,
  meet: Meet
) {
  let bodyWeight = lifter.bodyWeight;

  if (!bodyWeight) {
    return 0;
  }

  if (total <= 0) {
    return 0;
  }

  if (isKgMeet(meet)) {
    bodyWeight = toLbs(bodyWeight);
  }

  const genderIndex = lifter.gender === "FEMALE" ? 2 : 1;

  const compCode = competitionCode(division);
  const liftIndexParams = {
    PL: 0,
    SQ: 3, // Would be 4 if you want actual squats. 3 is an override to make this calculate as strict curl
    BP: 1,
    DL: 2,
    PP: 0,
  };

  const liftIndex =
    liftIndexParams[compCode as "PL" | "SQ" | "BP" | "DL" | "PP"] || 0;

  const ind = (genderIndex - 1) * 5 + liftIndex;
  const min_w_array = [
    80.0, 80.0, 80.0, 80.0, 80.0, 80.0, 80.0, 80.0, 80.0, 80.0,
  ];
  const max_w_array = [
    350.0, 350.0, 330.0, 300.0, 330.0, 250.0, 250.0, 250.0, 240.0, 250.0,
  ];
  const cm5_array = [
    -2.807e-9, 4.0e-12, -1.277e-9, -3.25e-10, -9.83e-10, -8.234e-9, 2.92e-10,
    -3.965e-9, -1.486e-9, -5.065e-9,
  ];
  const cm4_array = [
    3.060249e-6, 2.6046e-8, 1.3758e-6, 3.56667e-7, 1.070056e-6, 6.44957e-6,
    -3.1842e-7, 3.259889e-6, 1.113455e-6, 4.021973e-6,
  ];
  const cm3_array = [
    -1.247564847e-3, -2.2096307e-5, -5.53184164e-4, -1.44211677e-4,
    -4.37984879e-4, -1.853092021e-3, 1.35355511e-4, -9.93519656e-4,
    -3.08730794e-4, -1.194351346e-3,
  ];
  const cm2_array = [
    2.252637144e-1, 4.154784778e-3, 9.854582022e-2, 2.540099377e-2,
    7.9973211e-2, 2.290538594e-1, -2.894483536e-2, 1.341518785e-1,
    3.80382033e-2, 1.606593791e-1,
  ];
  const cm1_array = [
    -1.434283973e1, 6.510188629e-1, -6.340092282, -1.564614892, -5.226337856,
    -9.032322597, 3.326277127, -6.856470087, -1.797316824, -8.751469179,
  ];
  const cm0_array = [
    3.60792968e2, -3.43453458e1, 1.697756926e2, 4.026562795e1, 1.308265519e2,
    9.526623811e1, -1.085790099e2, 1.39138642e2, 3.477251117e1, 1.742483554e2,
  ];
  const csln_array = [
    4.3216e1, 1.6233e1, 1.7404e1, 4.4577, 2.0365e1, 2.8813e1, 7.9084, 1.0339e1,
    2.084, 1.4784e1,
  ];
  const cs0_array = [
    -1.3642e2, -5.8206e1, -5.2806e1, -1.3014e1, -7.0573e1, -9.1251e1, -2.6342e1,
    -2.8299e1, -4.4381, -5.1401e1,
  ];

  const min_w = min_w_array[ind];
  const max_w = max_w_array[ind];
  const cm5 = cm5_array[ind];
  const cm4 = cm4_array[ind];
  const cm3 = cm3_array[ind];
  const cm2 = cm2_array[ind];
  const cm1 = cm1_array[ind];
  const cm0 = cm0_array[ind];
  const csln = csln_array[ind];
  const cs0 = cs0_array[ind];
  const w = Math.max(Math.min(max_w, bodyWeight), min_w);
  const w2 = w * w;
  const w3 = w2 * w;
  const w4 = w3 * w;
  const w5 = w4 * w;
  const expected = w5 * cm5 + w4 * cm4 + w3 * cm3 + w2 * cm2 + w * cm1 + cm0;
  const std = Math.log(w) * csln + cs0;
  const mult = 100.0 / std;
  const add = 600.0 - (100.0 * expected) / std;

  const coeffs = [mult, add];

  if (isLbsMeet(meet)) {
    total = toKg(total);
  }

  return total * coeffs[0] + coeffs[1];
};

export const getKPoints: GetScore = ({
  lifter,
  division,
  meet,
  forecasted,
}) => {
  let total = getTotal({ lifter, division, meet, forecasted });
  if (isLbsMeet(meet)) {
    total = toKg(total);
  }

  return kPointsFormula(total, division, lifter, meet);
};

export const getKAndAgePoints: GetScore = ({
  lifter,
  division,
  meet,
  forecasted,
}) => {
  return (
    getKPoints({ lifter, division, meet, forecasted }) * ageCoef(lifter, meet)
  );
};
